import React, { FC, useState, useReducer, useEffect } from 'react'
import {
  Grid,
  DropdownSortOptions,
  Column,
  Filter,
  ValidFilterValues,
  GridSelectionMethods,
  Pagination
} from '@120wateraudit/envirio-components'
import { CheckboxProps } from 'semantic-ui-react'
import { DocumentNode } from 'graphql'
import { useQuery, QueryHookOptions, ApolloClient } from '@apollo/client'
import { contactsClient } from 'src/apollo-clients'

interface Props {
  client?: ApolloClient<any>
  query: DocumentNode
  queryName: string
  variables?: QueryHookOptions['variables']
  hideTimeInDateTimeFilter?: boolean
  breakFiltersVertically?: boolean
  columns?: Column[]
  filters?: any
  sorted?: {
    [key in string]: 'ASC' | 'DESC'
  }
  selectedFilters?: any
  availableFilters: any
  filtersArray?: Filter[]
  filterHeader?: string
  searchValue?: string
  searchPlaceholder?: string
  entityName?: string
  pluralEntityName?: string
  noResultsFound?: JSX.Element
  topRightFilter?: Filter
  topRightFilterState?: ValidFilterValues | undefined
  fetchDataElement?: JSX.Element
  displaySearch?: boolean
  sortMainFilterOptions?: DropdownSortOptions
  actionItems?: React.ReactNode
  renderTable?(data: any[]): JSX.Element
  style?: any
  unstackable?: boolean
  renderRow?: (rowData: any, index?: number) => JSX.Element | any
  totalGetter?(data: any): number
  searchRowElement?: JSX.Element
  gridLoader?: JSX.Element
  loaderRows?: number
  selectable?: boolean
  selectionKey?: string
  selectedItems?: any[]
  onPageChanged: (refetch: () => void) => void
  onColumnClicked: (refetch: () => void) => (column: Column) => void
  onFilterApplied: (
    refetch: () => void
  ) => (
    filter: Filter,
    value: ValidFilterValues,
    operator: string,
    value2: any
  ) => void
  onFilterRemoveClicked: (refetch: () => void) => (filter: Filter) => void
  onTopRightFilterChanged: any
  onRowSelected?: (
    checkboxInfo: CheckboxProps,
    data: any,
    index: number
  ) => void
  onSelectedItemsChange?: (selectedItems: any[]) => void
  onToggleSelectionColumn?: (checkboxInfo: CheckboxProps, data: any[]) => void
  additionalMenuItems?: React.ReactNode | undefined
  titleCaseShowingText?: boolean
  renderTableSuperHeader?(props: Props): JSX.Element | string | null
  renderTableSubHeader?(
    props: Props,
    selectionMethods: GridSelectionMethods
  ): JSX.Element | string | null
  hideShowingText?: boolean
  searchBarStyle?: React.CSSProperties
  page: number
  pageSize: number
  onPageSizeChanged: (refetch: () => void) => void
  queryVariablesTransformer?: (vars: any) => any
  pollInterval?: number
}

interface State {
  items: any[]
  total: number
  totalPages: number
}

enum DispatchType {
  INITIALIZE,
  UPDATE
}

interface InitializeGridAction {
  type: DispatchType.INITIALIZE
  payload: GridData
}

interface UpdateGridAction {
  type: DispatchType.UPDATE
  payload: GridData
}

interface GridData {
  [key: string]: {
    total: number
    count?: number
    totalPages: number
    pages?: number
    pageSize: number
    pageNumber: number
    items: any[]
  }
}

type Actions = InitializeGridAction | UpdateGridAction

export const HooksGridContainer: FC<Props> = ({
  client,
  query,
  queryName,
  variables,
  filters,
  availableFilters,
  selectedFilters,
  onFilterApplied,
  onFilterRemoveClicked,
  displaySearch,
  columns,
  sorted,
  renderRow,
  noResultsFound,
  entityName,
  pluralEntityName,
  additionalMenuItems,
  topRightFilter,
  onTopRightFilterChanged,
  onColumnClicked,
  page,
  pageSize,
  onPageChanged,
  onPageSizeChanged,
  queryVariablesTransformer,
  pollInterval
}) => {
  const contentReducer = (state: State, action: Actions): State => {
    if (
      action.payload &&
      action.payload[`${queryName}`].count !== undefined &&
      action.payload[`${queryName}`].total === undefined
    ) {
      action.payload[`${queryName}`].total = action.payload[`${queryName}`]
        .count as number
    }

    if (
      action.payload &&
      action.payload[`${queryName}`].pages !== undefined &&
      action.payload[`${queryName}`].totalPages === undefined
    ) {
      action.payload[`${queryName}`].totalPages = action.payload[`${queryName}`]
        .pages as number
    }

    if (action.type === DispatchType.INITIALIZE) {
      return {
        ...state,
        items: action.payload[`${queryName}`].items,
        total: action.payload[`${queryName}`].total,
        totalPages: action.payload[`${queryName}`].totalPages
      }
    }
    if (action.type === DispatchType.UPDATE) {
      return {
        ...state,
        items: action.payload
          ? action.payload[`${queryName}`].items
          : state.items,
        total: action.payload
          ? action.payload[`${queryName}`].total
          : state.total,
        totalPages: action.payload
          ? action.payload[`${queryName}`].totalPages
          : state.totalPages
      }
    }
    return state
  }

  const [searchValue, setSearchValue] = useState<string>('')
  const [state, dispatch] = useReducer(contentReducer, {
    items: [],
    total: 0,
    totalPages: 0
  })

  let transformedVariables = {
    ...variables,
    params: {
      pageSize,
      pageNumber: page,
      sorts: sorted,
      filters,
      search: searchValue
    }
  }

  if (queryVariablesTransformer) {
    transformedVariables = queryVariablesTransformer(transformedVariables)
  }

  const { loading, data, refetch } = useQuery(query, {
    variables: transformedVariables,
    client: client || contactsClient,
    pollInterval,
    onCompleted: payload => dispatch({ type: DispatchType.INITIALIZE, payload })
  })

  useEffect(() => {
    dispatch({ type: DispatchType.UPDATE, payload: data })
  }, [data])

  const handleSearchChanged = (refetch: () => void) => (e: any) => {
    setSearchValue(e.target.value)
    refetch()
  }

  return (
    <>
      <Grid
        isFetching={loading || !data}
        filters={availableFilters}
        selectedFilters={selectedFilters}
        onFilterApplied={onFilterApplied(refetch)}
        onFilterRemoveClicked={onFilterRemoveClicked(refetch)}
        style={{ margin: '2rem 0' }}
        data={state.items}
        displaySearch={displaySearch}
        count={state.total}
        columns={columns}
        sorted={sorted}
        searchValue={searchValue}
        renderRow={renderRow}
        noResultsFound={noResultsFound}
        onColumnClicked={onColumnClicked(refetch)}
        onSearchChanged={handleSearchChanged(refetch)}
        entityName={entityName || 'item'}
        pluralEntityName={pluralEntityName || 'items'}
        additionalMenuItems={additionalMenuItems}
        topRightFilter={topRightFilter}
        onTopRightFilterChanged={onTopRightFilterChanged(refetch)}
      />
      <Pagination
        page={page + 1}
        pageSize={pageSize}
        totalPages={state.totalPages}
        onPageChanged={onPageChanged(refetch)}
        onPageSizeChanged={onPageSizeChanged(refetch)}
      />
    </>
  )
}
